import { Currency } from "@redotech/money/currencies";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** One selectable variant option shown under an item (name/value pair). */
export const PosSelectItemsVariantSchema = z.object({
  name: z.string(),
  value: z.string(),
});

/**
 * One row in the select-items list, all selection-INDEPENDENT and computed
 * server-side. Client overlays live selection + try-first disable at render.
 */
export const PosSelectItemsItemSchema = z.object({
  id: z.string(),
  pendingReturnItemId: z.string(),
  imageSrc: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  quantity: z.number(),
  variants: z.array(PosSelectItemsVariantSchema),
  formattedPrice: z.string(),
  currency: createEnum(Currency),
  price: z.string(),
  itemValue: z.string().nullable(),
  /** Base pre-rejection eligibility (from the return flow); no selection input. */
  isPreRejected: z.boolean(),
  rejectMessage: z.string().nullable(),
  isNonReturnable: z.boolean(),
  nonReturnableReasons: z.string().nullable(),
  presentmentCurrency: z.string(),
  exchangeRate: z.number(),
  variantId: z.string().nullable(),
  collections: z.array(z.string()),
  /** Initial selection seed (from the draft return). */
  selected: z.boolean(),
  orderId: z.string(),
});

/**
 * Precomputed sibling relationships so the client toggle is a pure lookup.
 * `returnTogether` takes precedence; `bundleInstanceKey` resolves CUSTOMER_CHOICE.
 */
export const PosSelectItemsSiblingGroupSchema = z.object({
  itemId: z.string(),
  returnTogetherSiblingIds: z.array(z.string()),
  bundleSiblingIds: z.array(z.string()),
  bundleInstanceKey: z.string().nullable(),
});

/** One order section: raw name/date so the client localizes and formats. */
export const PosSelectItemsOrderSchema = z.object({
  id: z.string(),
  orderName: z.string(),
  orderDateISO: z.string(),
  items: z.array(PosSelectItemsItemSchema),
});

/**
 * Display-ready select-items screen state, computed server-side (replaces the
 * client useSelectItems display fork; interactive selection stays client).
 */
export const PosSelectItemsViewSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
  returnType: createEnum(ReturnTypeEnum),
  disablePreRejectedItems: z.boolean(),
  ordersWithItems: z.array(PosSelectItemsOrderSchema),
  siblingGroups: z.array(PosSelectItemsSiblingGroupSchema),
});

export type PosSelectItemsView = z.infer<typeof PosSelectItemsViewSchema>;
export type PosSelectItemsItem = z.infer<typeof PosSelectItemsItemSchema>;
export type PosSelectItemsOrder = z.infer<typeof PosSelectItemsOrderSchema>;
export type PosSelectItemsSiblingGroup = z.infer<
  typeof PosSelectItemsSiblingGroupSchema
>;

export const getPosSelectItemsView = {
  name: "getPosSelectItemsView" as const,
  input: z.object({ draftReturnId: z.string() }),
  output: z.object({ view: PosSelectItemsViewSchema }),
};
